import { motion } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';

import { ACCENT_BLUE, CONNECTIONS, DARK_BG, EVIL_RED, GOLD, MUTED_GRAY, NOPING_GREEN } from '../constants';
import { ThemedCard } from '../ui-helpers';

export function MultiInternetLiveDemo() {
  const { t } = useTranslation(['components/trial/lp/v4d']);
  const [connStatus, setConnStatus] = useState<boolean[]>(() => CONNECTIONS.map(() => true));
  const [pings, setPings] = useState<number[]>(() => CONNECTIONS.map((c) => c.basePing));
  const [pingHistory, setPingHistory] = useState<number[][]>(() => CONNECTIONS.map((c) => Array(20).fill(c.basePing) as number[]));

  useEffect(() => {
    const dropInterval = setInterval(() => {
      setConnStatus((prev) => {
        const next = [...prev];
        const activeCount = next.filter(Boolean).length;
        const idx = Math.floor(Math.random() * CONNECTIONS.length);
        if (!next[idx]) {
          next[idx] = true;
          return next;
        }
        if (activeCount > 3) {
          next[idx] = false;
        }
        return next;
      });
    }, 2500);

    const pingInterval = setInterval(() => {
      const newPings = CONNECTIONS.map((c) => {
        const jitter = Math.floor(Math.random() * 6) - 2;
        return Math.max(1, c.basePing + jitter);
      });
      setPings(newPings);
      setPingHistory((prev) => prev.map((hist, i) => [...hist.slice(-19), newPings[i]!]));
    }, 1200);

    return () => {
      clearInterval(dropInterval);
      clearInterval(pingInterval);
    };
  }, []);

  const activeCount = connStatus.filter(Boolean).length;
  const activePings = pings.filter((_, i) => connStatus[i]);
  const bestPing = activePings.length > 0 ? Math.min(...activePings) : 0;

  const W = 700;
  const H = 260;
  const centerY = H / 2;
  const ispBoxX = 10;
  const ispBoxW = 82;
  const statusX = ispBoxX + ispBoxW + 6;
  const pingBadgeX = statusX + 32;
  const sparkX = pingBadgeX + 40;
  const sparkW = 80;
  const convergeEndX = sparkX + sparkW + 8;
  const shieldX = 490;
  const outputX = 630;
  const laneGap = 36;
  const firstLane = 24;
  const laneYs = Array.from({ length: 6 }, (_, i) => firstLane + i * laneGap);

  return (
    <div className='mb-6'>
      <ThemedCard className='p-3 md:p-5' glow={ACCENT_BLUE}>
        <div className='mb-3 flex items-center justify-between'>
          <span className='text-[10px] font-semibold uppercase tracking-widest md:text-xs' style={{ color: ACCENT_BLUE, fontFamily: 'var(--font-outfit)' }}>
            {t('multi_internet.engine_label')}
          </span>
          <div className='flex items-center gap-2'>
            <motion.div className='size-1.5 rounded-full' style={{ backgroundColor: NOPING_GREEN }} animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' as const }} />
            <span className='rounded border px-2 py-0.5 text-[10px] font-bold' style={{ color: NOPING_GREEN, borderColor: `${NOPING_GREEN}40`, backgroundColor: `${NOPING_GREEN}08`, fontFamily: 'var(--font-outfit)' }}>
              {t('multi_internet.isps_active', { total: activeCount })}
            </span>
          </div>
        </div>

        <div className='overflow-hidden rounded-lg' style={{ backgroundColor: DARK_BG, border: `1px solid ${ACCENT_BLUE}10` }}>
          <svg viewBox={`0 0 ${W} ${H}`} className='w-full' preserveAspectRatio='xMidYMid meet'>
            <defs>
              <filter id='miGlow'>
                <feGaussianBlur stdDeviation='3' result='b' />
                <feMerge>
                  <feMergeNode in='b' />
                  <feMergeNode in='b' />
                  <feMergeNode in='SourceGraphic' />
                </feMerge>
              </filter>
              <filter id='shieldGlow'>
                <feGaussianBlur stdDeviation='4' result='b' />
                <feMerge>
                  <feMergeNode in='b' />
                  <feMergeNode in='SourceGraphic' />
                </feMerge>
              </filter>
              <linearGradient id='shieldGrad' x1='0' y1='0' x2='0' y2='1'>
                <stop offset='0%' stopColor={NOPING_GREEN} stopOpacity='0.15' />
                <stop offset='100%' stopColor={NOPING_GREEN} stopOpacity='0.03' />
              </linearGradient>
            </defs>

            <g filter='url(#shieldGlow)'>
              <path d={`M${shieldX} ${centerY - 42} L${shieldX + 32} ${centerY - 24} L${shieldX + 32} ${centerY + 16} Q${shieldX + 32} ${centerY + 42} ${shieldX} ${centerY + 50} Q${shieldX - 32} ${centerY + 42} ${shieldX - 32} ${centerY + 16} L${shieldX - 32} ${centerY - 24} Z`} fill='url(#shieldGrad)' stroke={NOPING_GREEN} strokeWidth='1.2' />
              <text x={shieldX} y={centerY + 6} textAnchor='middle' fill={NOPING_GREEN} fontSize='20' fontFamily='var(--font-bai)' fontWeight='800' letterSpacing='1'>
                NP
              </text>
              <motion.circle cx={shieldX} cy={centerY + 3} r='40' fill='none' stroke={NOPING_GREEN} strokeWidth='0.6' animate={{ r: [40, 54, 40], opacity: [0.2, 0, 0.2] }} transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' as const }} />
              <text x={shieldX} y={centerY + 66} textAnchor='middle' fill={NOPING_GREEN} fontSize='7' fontFamily='var(--font-outfit)' fontWeight='700' letterSpacing='0.5'>
                MULTI-INTERNET
              </text>
              <text x={shieldX} y={centerY + 75} textAnchor='middle' fill={MUTED_GRAY} fontSize='5.5' fontFamily='var(--font-outfit)'>
                {t('multi_internet.patented')}
              </text>
            </g>

            {(() => {
              const x1 = shieldX + 36;
              const x2 = outputX - 32;
              const bridgeColors = [NOPING_GREEN, ACCENT_BLUE, NOPING_GREEN, ACCENT_BLUE, ACCENT_BLUE];
              const bridgeYOffsets = [-20, -10, 0, 10, 20];
              return bridgeColors.map((bColor, bi) => {
                const yOff = bridgeYOffsets[bi]!;
                const bridgePath = `M ${x1} ${centerY + yOff} L ${x2} ${centerY + yOff}`;
                const motionDur = 1 + bi * 0.1;
                const cycleDur = motionDur + 0.5;
                return (
                  <g key={`out-bridge-${bi}`}>
                    <path d={bridgePath} fill='none' stroke={bColor} strokeWidth={bi === 2 ? 1.5 : 0.7} strokeOpacity={bi === 2 ? 0.5 : 0.2} strokeDasharray={bi === 2 ? 'none' : '4 3'} />
                    <circle r={bi === 2 ? 3.5 : 2} fill={bColor} filter='url(#miGlow)'>
                      <animateMotion dur={`${cycleDur}s`} begin={`${bi * 0.15}s`} repeatCount='indefinite' path={bridgePath} keyTimes={`0;${motionDur / cycleDur};1`} keyPoints='0;1;1' />
                    </circle>
                  </g>
                );
              });
            })()}

            <g>
              <rect x={outputX - 34} y={centerY - 28} width='68' height='48' rx='5' fill={`${NOPING_GREEN}06`} stroke={NOPING_GREEN} strokeWidth='1.2' />
              <rect x={outputX - 28} y={centerY - 22} width='56' height='34' rx='3' fill={`${NOPING_GREEN}04`} stroke={NOPING_GREEN} strokeWidth='0.5' strokeOpacity='0.5' />
              <line x1={outputX - 8} y1={centerY - 5} x2={outputX + 8} y2={centerY - 5} stroke={NOPING_GREEN} strokeWidth='1' opacity='0.6' />
              <line x1={outputX} y1={centerY - 13} x2={outputX} y2={centerY + 3} stroke={NOPING_GREEN} strokeWidth='1' opacity='0.6' />
              <circle cx={outputX} cy={centerY - 5} r='6' fill='none' stroke={NOPING_GREEN} strokeWidth='0.7' opacity='0.4' />
              <circle cx={outputX} cy={centerY - 5} r='2' fill={NOPING_GREEN} opacity='0.5' />
              <line x1={outputX} y1={centerY + 20} x2={outputX} y2={centerY + 30} stroke={NOPING_GREEN} strokeWidth='1.5' opacity='0.5' />
              <line x1={outputX - 14} y1={centerY + 30} x2={outputX + 14} y2={centerY + 30} stroke={NOPING_GREEN} strokeWidth='1.2' opacity='0.4' />
              <motion.circle cx={outputX} cy={centerY + 16} r='1.5' fill={NOPING_GREEN} animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' as const }} />
              <text x={outputX} y={centerY + 42} textAnchor='middle' fill={NOPING_GREEN} fontSize='6' fontFamily='var(--font-outfit)' fontWeight='700'>
                0% LOSS
              </text>
              <text x={outputX} y={centerY + 54} textAnchor='middle' fill={NOPING_GREEN} fontSize='9' fontFamily='var(--font-outfit)' fontWeight='700'>
                GAME
              </text>
              <text x={outputX} y={centerY + 63} textAnchor='middle' fill={MUTED_GRAY} fontSize='6' fontFamily='var(--font-outfit)'>
                {bestPing}ms
              </text>
            </g>

            {CONNECTIONS.map((conn, i) => {
              const ly = laneYs[i]!;
              const isActive = connStatus[i];
              const ping = pings[i];
              const color = isActive ? NOPING_GREEN : EVIL_RED;

              const sparkPoints = (pingHistory[i] ?? [])
                .map((p, pi) => {
                  const sx = sparkX + (pi / 19) * sparkW;
                  const sy = ly - 9 + 18 - (Math.min(p, 40) / 40) * 18;
                  return `${sx},${sy}`;
                })
                .join(' ');

              const pathToShield = `M ${convergeEndX} ${ly} C ${convergeEndX + 60} ${ly}, ${shieldX - 80} ${centerY}, ${shieldX - 34} ${centerY}`;

              return (
                <g key={`mi-${i}`}>
                  <rect x={ispBoxX} y={ly - 14} width={ispBoxW} height='28' rx='5' fill={`${color}08`} stroke={color} strokeWidth='0.6' strokeOpacity={isActive ? 0.4 : 0.2} />
                  <svg x={ispBoxX + 6} y={ly - 7} width='14' height='14' viewBox='0 0 24 24' fill='none' stroke={color} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                    <path d={conn.icon} />
                  </svg>
                  <text x={ispBoxX + 22} y={ly + 3} fill={color} fontSize='8' fontFamily='var(--font-outfit)' fontWeight='600' opacity={isActive ? 0.9 : 0.4}>
                    {conn.name}
                  </text>

                  <rect x={statusX} y={ly - 8} width='28' height='16' rx='8' fill={`${isActive ? NOPING_GREEN : EVIL_RED}12`} stroke={isActive ? NOPING_GREEN : EVIL_RED} strokeWidth='0.4' strokeOpacity='0.4' />
                  <motion.circle cx={statusX + 8} cy={ly} r='2.5' fill={isActive ? NOPING_GREEN : EVIL_RED} animate={{ opacity: isActive ? [1, 0.4, 1] : [0.6, 0.2, 0.6] }} transition={{ duration: isActive ? 2 : 0.6, repeat: Infinity, ease: 'easeInOut' as const }} />
                  <text x={statusX + 19} y={ly + 3} textAnchor='middle' fill={isActive ? NOPING_GREEN : EVIL_RED} fontSize='6' fontFamily='var(--font-outfit)' fontWeight='700'>
                    {isActive ? 'ON' : 'OFF'}
                  </text>

                  <rect x={pingBadgeX} y={ly - 8} width='34' height='16' rx='8' fill={`${color}10`} stroke={color} strokeWidth='0.4' strokeOpacity='0.3' />
                  <text x={pingBadgeX + 17} y={ly + 3} textAnchor='middle' fill={color} fontSize='7' fontFamily='var(--font-outfit)' fontWeight='700'>
                    {isActive ? `${ping}ms` : '\u2014'}
                  </text>

                  <polyline points={sparkPoints} fill='none' stroke={color} strokeWidth='1.2' strokeOpacity={isActive ? 0.55 : 0.12} strokeLinejoin='round' />
                  <circle cx={sparkX + sparkW} cy={parseFloat(sparkPoints.split(' ').pop()?.split(',')[1] ?? String(ly))} r={isActive ? 2.5 : 1} fill={color} opacity={isActive ? 0.9 : 0.3} />

                  <path d={pathToShield} fill='none' stroke={color} strokeWidth={isActive ? 1 : 0.5} strokeOpacity={isActive ? 0.2 : 0.06} strokeDasharray={isActive ? 'none' : '3 2'} />

                  {isActive &&
                    (() => {
                      const motionDur = 1.8 + i * 0.12;
                      const cycleDur = motionDur + 0.4;
                      return (
                        <circle r='2.5' fill={color} filter='url(#miGlow)'>
                          <animateMotion dur={`${cycleDur}s`} begin={`${i * 0.25}s`} repeatCount='indefinite' path={pathToShield} keyTimes={`0;${motionDur / cycleDur};1`} keyPoints='0;1;1' />
                        </circle>
                      );
                    })()}
                </g>
              );
            })}
          </svg>
        </div>

        <div className='mt-3 grid grid-cols-2 gap-2 md:grid-cols-4'>
          {[
            { value: `${activeCount}/6`, label: t('multi_internet.stat_isps'), color: ACCENT_BLUE },
            { value: `${bestPing}ms`, label: t('multi_internet.stat_ping'), color: NOPING_GREEN },
            { value: '0%', label: t('multi_internet.stat_loss'), color: NOPING_GREEN },
            { value: 'BR 102015016756-3', label: t('multi_internet.stat_patent'), color: GOLD },
          ].map((s, i) => (
            <div key={i} className='rounded-lg px-2 py-1.5 text-center' style={{ backgroundColor: `${s.color}06`, border: `1px solid ${s.color}12` }}>
              <p className='break-words text-sm font-bold tabular-nums md:text-base' style={{ color: s.color, fontFamily: 'var(--font-bai)' }}>
                {s.value}
              </p>
              <p className='text-[9px] font-medium' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </ThemedCard>
    </div>
  );
}
